import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;
  #activeRequestKey = null;

  #pageProductId() {
    return document.querySelector('#MainContent product-form-component[data-product-url]')?.dataset.productId;
  }

  // A restored section may still belong to a previously viewed colourway.
  #syncProductContext() {
    const productId = this.#pageProductId();
    if (!productId) return false;

    let removedCurrentProduct = false;
    for (const card of this.querySelectorAll('product-card[data-product-id]')) {
      if (card.dataset.productId !== productId) continue;
      (card.closest('slideshow-slide') || card.closest('.resource-list__item') || card).remove();
      removedCurrentProduct = true;
    }

    if (this.dataset.productId === productId && !removedCurrentProduct) return false;

    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#cachedRecommendations = {};
    this.#mutationObserver.disconnect();
    this.dataset.productId = productId;
    this.dataset.recommendationsPerformed = 'false';
    this.#mutationObserver.observe(this, { attributes: true });
    return true;
  }

  #onPageShow = () => {
    if (this.#syncProductContext()) this.#loadRecommendations();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    window.addEventListener('pageshow', this.#onPageShow);
    this.#onPageShow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    this.#syncProductContext();
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }
    const requestKey = [this.dataset.url, productId, sectionId, intent].join('|');
    if (this.#activeFetch && this.#activeRequestKey === requestKey) return;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        // Ignore a response for a watch whose page has already been left.
        if (!this.isConnected || this.dataset.productId !== productId) return;
        const pageProductId = this.#pageProductId();
        if (pageProductId && pageProductId !== productId) return;
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    this.#activeRequestKey = [this.dataset.url, productId, sectionId, intent].join('|');
    const controller = this.#activeFetch;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
        this.#activeRequestKey = null;
      }
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
